import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";

const DAYS = ["D", "L", "M", "M", "J", "V", "S"];
const MONTHS = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

const parseDate = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const toDateValue = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const formatDisplayDate = (value) => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
};

const ModernDatePicker = ({ value, onChange, min, max, disabled = false, ariaLabel }) => {
  const initialDate = parseDate(value) || parseDate(min) || new Date();
  const [visibleMonth, setVisibleMonth] = useState(
    new Date(initialDate.getFullYear(), initialDate.getMonth(), 1),
  );
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const referenceDate = parseDate(value) || parseDate(min);
    if (!referenceDate) return;

    setVisibleMonth(
      new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1),
    );
  }, [value, min]);

  useEffect(() => {
    const closeCalendar = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const closeWithEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", closeCalendar);
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.removeEventListener("mousedown", closeCalendar);
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, []);

  const cells = useMemo(() => {
    const year = visibleMonth.getFullYear();
    const month = visibleMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return [
      ...Array(firstDay).fill(null),
      ...Array.from({ length: daysInMonth }, (_, index) => new Date(year, month, index + 1)),
    ];
  }, [visibleMonth]);

  const minDate = parseDate(min);
  const maxDate = parseDate(max);
  const firstYear = minDate?.getFullYear() || new Date().getFullYear();
  const lastYear = maxDate?.getFullYear() || firstYear + 10;
  const availableYears = Array.from(
    { length: lastYear - firstYear + 1 },
    (_, index) => firstYear + index,
  );
  const previousMonth = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth() - 1,
    1,
  );
  const nextMonth = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth() + 1,
    1,
  );
  const previousDisabled = minDate && new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 0) < minDate;
  const nextDisabled = maxDate && nextMonth > maxDate;

  const isMonthUnavailable = (year, month) => {
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    return (minDate && monthEnd < minDate) || (maxDate && monthStart > maxDate);
  };

  return (
    <div ref={containerRef} className="relative mt-1 w-full">
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setIsOpen((open) => !open)}
          className="flex w-full items-center rounded-xl border border-gray-200 bg-white p-3 pr-20 text-left shadow-sm transition-all duration-200 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:cursor-not-allowed disabled:bg-gray-100"
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          <span className={value ? "text-gray-900" : "text-gray-400"}>
            {formatDisplayDate(value) || "JJ/MM/AAAA"}
          </span>
        </button>

        {value && !disabled && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-10 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Effacer la date"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <CalendarDays className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
      </div>

      {isOpen && !disabled && (
        <div
          className="absolute left-0 z-50 mt-2 w-72 rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl"
          role="dialog"
          aria-label={ariaLabel}
        >
          <div className="mb-4 flex items-center justify-between gap-2">
            <button
              type="button"
              disabled={previousDisabled}
              onClick={() => setVisibleMonth(previousMonth)}
              className="rounded-lg p-2 text-gray-600 transition hover:bg-gray-100 disabled:text-gray-300"
              aria-label="Mois précédent"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex flex-1 items-center justify-center gap-2">
              <select
                value={visibleMonth.getMonth()}
                onChange={(event) =>
                  setVisibleMonth(
                    new Date(visibleMonth.getFullYear(), Number(event.target.value), 1),
                  )
                }
                className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm font-semibold text-blue-900 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                aria-label="Choisir le mois"
              >
                {MONTHS.map((month, index) => (
                  <option
                    key={month}
                    value={index}
                    disabled={isMonthUnavailable(visibleMonth.getFullYear(), index)}
                  >
                    {month}
                  </option>
                ))}
              </select>
              <select
                value={visibleMonth.getFullYear()}
                onChange={(event) => {
                  const year = Number(event.target.value);
                  let month = visibleMonth.getMonth();
                  if (isMonthUnavailable(year, month)) {
                    month = year === firstYear && minDate ? minDate.getMonth() : maxDate?.getMonth() || 0;
                  }
                  setVisibleMonth(new Date(year, month, 1));
                }}
                className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm font-semibold text-blue-900 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                aria-label="Choisir l’année"
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={nextDisabled}
              onClick={() => setVisibleMonth(nextMonth)}
              className="rounded-lg p-2 text-gray-600 transition hover:bg-gray-100 disabled:text-gray-300"
              aria-label="Mois suivant"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {DAYS.map((day, index) => (
              <span key={`${day}-${index}`} className="py-1 text-center text-xs font-semibold text-gray-500">
                {day}
              </span>
            ))}
            {cells.map((date, index) => {
              if (!date) return <span key={`empty-${index}`} />;
              const dateValue = toDateValue(date);
              const unavailable = (minDate && date < minDate) || (maxDate && date > maxDate);
              const selected = value === dateValue;
              const today = dateValue === toDateValue(new Date());

              return (
                <button
                  key={dateValue}
                  type="button"
                  disabled={unavailable}
                  onClick={() => {
                    onChange(dateValue);
                    setIsOpen(false);
                  }}
                  className={`h-9 rounded-lg text-sm transition ${
                    unavailable
                      ? "cursor-not-allowed bg-gray-100 text-gray-300"
                      : selected
                        ? "bg-purple-600 font-semibold text-white"
                        : today
                          ? "bg-purple-50 font-semibold text-purple-800 hover:bg-purple-100"
                          : "text-gray-700 hover:bg-purple-100 hover:text-purple-800"
                  }`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernDatePicker;
